<template>
  <div class="driver_order_stats_page">
    <!-- SECTION TITLE BAR -->
    <div class="stats_header_bar mb-4">
      <div class="title_group">
        <i class="fal fa-chart-pie icon_main"></i>
        <div>
          <h5 class="m-0 font-weight-bold">إحصائيات وأداء الطلبات</h5>
          <span class="text-muted text_small"
            >ملخص شامل لعمليات، أرباح، ومستحقات السائق</span
          >
        </div>
      </div>
    </div>

    <!-- LOADER STATE -->
    <div class="py-5 text-center" v-if="loading">
      <v-progress-circular
        indeterminate
        color="purple darken-2"
        size="50"
        width="4"
      ></v-progress-circular>
      <p class="mt-3 text-muted font-weight-bold">
        جاري تحميل إحصائيات السائق...
      </p>
    </div>

    <!-- EMPTY STATE CONTAINER (WHEN NO DATA AVAILABLE) -->
    <div
      class="empty_stats_card py-5 px-4 text-center my-4"
      v-else-if="!loading && !allData"
    >
      <div class="empty_icon_wrapper mx-auto mb-4">
        <div class="empty_icon_box">
          <i class="fal fa-chart-bar"></i>
        </div>
        <div class="pulse_ring"></div>
      </div>
      <h4 class="empty_title font-weight-bold mb-2">لا توجد إحصائيات مجهزة</h4>
      <p
        class="empty_subtitle text-muted mx-auto mb-4"
        style="max-width: 480px"
      >
        تعذر جلب إحصائيات السائق حالياً أو لا تتوفر أي بيانات طلبات مخصصة.
      </p>
    </div>

    <!-- CREATIVE STATS GRID -->
    <div class="row g-4" v-else-if="allData">
      <div
        v-for="(card, index) in statsCards"
        :key="index"
        class="col-xl-4 col-lg-4 col-md-6 col-12 mb-4"
      >
        <component
          :is="card.link ? 'router-link' : 'div'"
          :to="card.link"
          class="creative_stat_card"
          :class="{ is_clickable: !!card.link }"
        >
          <div
            class="card_icon_box"
            :style="{ background: card.bg, color: card.color }"
          >
            <i :class="card.icon"></i>
          </div>

          <div class="card_info">
            <span class="stat_label">{{ card.title }}</span>
            <h3 class="stat_value" :style="{ color: card.color }">
              {{ card.value != null ? card.value : 0 }}
              <span class="unit_lbl" v-if="card.unit">{{ card.unit }}</span>
            </h3>
          </div>

          <div class="action_arrow_icon" v-if="card.link">
            <i class="fal fa-arrow-left"></i>
          </div>
        </component>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["userInfo"],

  data() {
    return {
      allData: null,
      loading: false,
    };
  },

  computed: {
    statsCards() {
      if (!this.allData) return [];
      const id = this.userInfo?.id || this.$route.params.id;
      return [
        {
          title: "رصيد المحفظة",
          value: this.allData.balance,
          unit: "ر.س",
          icon: "fal fa-wallet",
          color: "#10b981",
          bg: "rgba(16, 185, 129, 0.12)",
        },
        {
          title: "طلبات التوصيل المنتهية",
          value: this.allData.deliveries_count,
          icon: "fal fa-shipping-fast",
          color: "#0284c7",
          bg: "rgba(2, 132, 199, 0.12)",
          link: `/driver/show/${id}/deliveryOrders?status=driver_finished_delivery`,
        },
        {
          title: "طلبات الطرود المنتهية",
          value: this.allData.packages_count,
          icon: "fal fa-box-open",
          color: "#d97706",
          bg: "rgba(217, 119, 6, 0.12)",
          link: `/driver/show/${id}/packageOrders?status=driver_finished_package`,
        },
        {
          title: "الطلبات المرفوضة",
          value: this.allData.rejected_count,
          icon: "fal fa-user-times",
          color: "#ef4444",
          bg: "rgba(239, 68, 68, 0.12)",
          link: `/driver/show/${id}/newOrders?status=driver_rejected`,
        },
        {
          title: "مقبولة من سائق آخر",
          value: this.allData.rejected_count_from_another_driver,
          icon: "fal fa-random",
          color: "#8b5cf6",
          bg: "rgba(139, 92, 246, 0.12)",
          link: `/driver/show/${id}/newOrders?status=order_taken_from_another_driver`,
        },
        {
          title: "إجمالي عدد الطلبات",
          value: this.allData.orders_count,
          icon: "fal fa-boxes",
          color: "#6366f1",
          bg: "rgba(99, 102, 241, 0.12)",
        },
        {
          title: "إجمالي قيمة التوصيل",
          value: this.allData.finished_orders_delivery_price_total,
          unit: "ر.س",
          icon: "fal fa-money-bill-wave",
          color: "#0d9488",
          bg: "rgba(13, 148, 136, 0.12)",
        },
        {
          title: "إجمالي تعويضات الشركة",
          value: this.allData.indemnification,
          unit: "ر.س",
          icon: "fal fa-hand-holding-usd",
          color: "#2563eb",
          bg: "rgba(37, 99, 235, 0.12)",
        },
        {
          title: "إجمالي البونص",
          value: this.allData.bonus,
          unit: "ر.س",
          icon: "fal fa-gift",
          color: "#f59e0b",
          bg: "rgba(245, 158, 11, 0.12)",
        },
        {
          title: "المستحق من السائق",
          value: this.allData.money_needed,
          unit: "ر.س",
          icon: "fal fa-exclamation-circle",
          color: "#dc2626",
          bg: "rgba(220, 38, 38, 0.12)",
        },
        {
          title: "مستحقات الشركة",
          value: this.allData.delegate_company_money,
          unit: "ر.س",
          icon: "fal fa-building",
          color: "#783d73",
          bg: "rgba(120, 61, 115, 0.12)",
        },
        {
          title: "متوسط تقييم السائق",
          value: this.allData.rate_avg,
          unit: "★",
          icon: "fal fa-star",
          color: "#eab308",
          bg: "rgba(234, 179, 8, 0.12)",
        },
      ];
    },
  },

  methods: {
    setRows() {
      const driverId = this.$route.params.id || this.userInfo?.id;
      if (!driverId) return;
      this.loading = true;
      this.$axios({
        method: "GET",
        url: "driver/" + driverId + "/statistics",
      })
        .then((res) => {
          this.allData = res.data.data;
          this.loading = false;
        })
        .catch(() => {
          this.allData = null;
          this.loading = false;
        });
    },
  },
  created() {
    this.setRows();
  },
};
</script>

<style lang="scss" scoped>
.driver_order_stats_page {
  /* SECTION TITLE BAR */
  .stats_header_bar {
    background: #ffffff;
    border-radius: 20px;
    padding: 18px 24px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);

    .title_group {
      display: flex;
      align-items: center;
      gap: 14px;

      .icon_main {
        font-size: 1.5rem;
        color: var(--mainColor-2, #783d73);
      }

      .text_small {
        font-size: 0.825rem;
      }
    }
  }

  /* EMPTY STATE */
  .empty_stats_card {
    background: #ffffff;
    border-radius: 24px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.04);
    padding: 60px 20px;

    .empty_icon_wrapper {
      position: relative;
      width: 90px;
      height: 90px;

      .empty_icon_box {
        width: 90px;
        height: 90px;
        border-radius: 26px;
        background: linear-gradient(
          135deg,
          rgba(120, 61, 115, 0.1) 0%,
          rgba(120, 61, 115, 0.04) 100%
        );
        border: 2px dashed rgba(120, 61, 115, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2.4rem;
        color: var(--mainColor-2, #783d73);
        position: relative;
        z-index: 2;
      }

      .pulse_ring {
        position: absolute;
        inset: -8px;
        border-radius: 30px;
        background: rgba(120, 61, 115, 0.08);
        z-index: 1;
        animation: pulseRing 2.5s infinite ease-in-out;
      }
    }

    .empty_title {
      font-size: 1.25rem;
      color: #1e293b;
    }

    .empty_subtitle {
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }

  /* STATS CARDS */
  .creative_stat_card {
    background: #ffffff;
    border-radius: 20px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    padding: 22px 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    text-decoration: none !important;
    position: relative;
    box-shadow: 0 8px 20px -5px rgba(15, 23, 42, 0.03);
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

    &.is_clickable {
      cursor: pointer;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 30px -5px rgba(15, 23, 42, 0.08);

        .action_arrow_icon {
          transform: translateX(-4px);
          opacity: 1;
        }
      }
    }

    .card_icon_box {
      width: 62px;
      height: 62px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.6rem;
      flex-shrink: 0;
      transition: transform 0.3s ease;
    }

    .card_info {
      flex-grow: 1;

      .stat_label {
        font-size: 0.825rem;
        color: #64748b;
        font-weight: 500;
        display: block;
        margin-bottom: 4px;
      }

      .stat_value {
        font-size: 1.4rem;
        font-weight: 800;
        margin: 0;
        line-height: 1.2;

        .unit_lbl {
          font-size: 0.85rem;
          font-weight: 600;
          margin-inline-start: 4px;
        }
      }
    }

    .action_arrow_icon {
      font-size: 0.9rem;
      color: #94a3b8;
      opacity: 0.6;
      transition: all 0.3s ease;
    }
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(0.95);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.6;
  }
}
</style>
